import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import { COMPANY_NAME, SITE_DOMAIN, SITE_NAME, SITE_URL } from '../constants/site';

const UPDATED = 'May 25, 2026';

export default function FareDisclosurePolicyPage() {
  return (
    <PolicyLayout title="Fare Disclosure Policy" lastUpdated={UPDATED}>
      <PolicySection title="1. Purpose">
        <p>
          This Fare Disclosure Policy explains how prices are displayed on {SITE_NAME} ({SITE_URL}), operated by{' '}
          {COMPANY_NAME}, and what is included in quoted fares. We comply with applicable U.S. advertising and consumer
          protection standards for total price disclosure where required.
        </p>
      </PolicySection>

      <PolicySection title="2. Total price display">
        <p>
          When required by law for advertised airfares, we present the full price per passenger including government
          taxes and mandatory carrier-imposed surcharges in the primary display. Optional fees (baggage, seats, upgrades)
          are disclosed separately because they depend on your selections and airline policy.
        </p>
      </PolicySection>

      <PolicySection title="3. Components of airfare">
        <PolicyList
          items={[
            'Base fare — the carrier’s charge for transportation before taxes and surcharges.',
            'Government taxes — U.S. and foreign taxes such as segment fees, facility charges, and security fees.',
            'Carrier-imposed surcharges — fuel, insurance, or international surcharges set by the airline.',
            'Agency service fees — charges by Farenodex for booking assistance, shown at checkout when applicable.',
          ]}
        />
      </PolicySection>

      <PolicySection title="4. Hotel, car, and package rates">
        <PolicySubsection title="4.1 Hotels">
          <p>
            Nightly rates may exclude resort fees, occupancy taxes, parking, and incidentals collected by the property at
            check-in. Prepaid vs. pay-at-property rates are labeled in search results.
          </p>
        </PolicySubsection>
        <PolicySubsection title="4.2 Cars">
          <p>
            Car quotes include estimated taxes and fees; optional insurance, young-driver fees, and fuel charges may
            apply at the counter per rental company policy.
          </p>
        </PolicySubsection>
        <PolicySubsection title="4.3 Packages and cruises">
          <p>
            Bundle pricing may combine air, hotel, and activities. Government taxes, port fees, gratuities, and onboard
            charges for cruises are disclosed in offer details when provided by the supplier.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="5. Currency">
        <p>
          Unless stated otherwise, prices are quoted in U.S. dollars (USD). If you pay with a card in another currency,
          your bank may apply conversion rates and foreign transaction fees outside our control.
        </p>
      </PolicySection>

      <PolicySection title="6. Price changes and errors">
        <PolicyList
          items={[
            'Fares are subject to change until ticketed or confirmed by the supplier.',
            'Obvious errors (e.g., $1 international business class) may be corrected and bookings canceled with refund.',
            'Advertised promotional fares may have capacity limits, blackout dates, and advance-purchase requirements.',
          ]}
        />
      </PolicySection>

      <PolicySection title="7. Baggage and optional services">
        <p>
          Many economy fares do not include checked baggage. Baggage fees, seat selection, priority boarding, and meals
          are governed by airline policies linked or summarized at checkout. Review fare rules before purchasing.
        </p>
      </PolicySection>

      <PolicySection title="8. Comparison shopping">
        <p>
          When comparing {SITE_DOMAIN} with other sites, ensure you match itinerary, fare brand, baggage allowance, and
          refundability. See our <Link to="/flight/price-match">Price Match Promise</Link> for eligible post-booking reviews.
        </p>
      </PolicySection>

      <PolicySection title="9. Related policies">
        <p>
          <Link to="/flight/service-fees">Service Fees</Link> · <Link to="/flight/advertiser-disclosure">Advertiser Disclosure</Link>{' '}
          · <Link to="/flight/terms">Terms &amp; Conditions</Link>
        </p>
      </PolicySection>

      <PolicySection title="10. Contact us">
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
